import {
  type BigDecimal,
  createBigDecimal,
  divide,
  equals,
  greaterThan,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { AssetBalance } from '../../../types/assetBalance'
import type { AssetBorrow } from '../../../types/assetBorrow'
import type { CostBasis } from '../../../types/costBasis'

type GetCostByAssetBalancesAndBorrowsParameters = {
  readonly amount: BigDecimal
  readonly assetBalances: readonly AssetBalance[]
  readonly assetBorrows: readonly AssetBorrow[]
  readonly usedBalance: AssetBalance
  readonly costBasis: CostBasis
}

type GetCostByAssetBalancesAndBorrowsReturnTypes = {
  readonly cost: BigDecimal
  readonly assetBalances: readonly AssetBalance[]
  readonly assetBorrows: readonly AssetBorrow[]
  readonly usedBalance: AssetBalance
}

export const getCostByAssetBalancesAndBorrows = ({
  amount,
  assetBalances,
  assetBorrows,
  usedBalance,
  costBasis,
}: GetCostByAssetBalancesAndBorrowsParameters): GetCostByAssetBalancesAndBorrowsReturnTypes => {
  // cost basis に応じて asset balance から　cost 取得 & asset balances , used balance 更新
  const {
    remain: remainByAssetBalances,
    cost: costByAssetBalances,
    assetBalances: newAssetBalances,
    usedBalance: newUsedBalance,
  } = getCostByAssetBalances({ amount, assetBalances, usedBalance, costBasis })

  // remain = 0 だったら asset borrow は使用せず値を返す
  if (remainByAssetBalances.value === 0n) {
    return {
      cost: costByAssetBalances,
      assetBalances: newAssetBalances,
      assetBorrows,
      usedBalance: newUsedBalance,
    }
  }

  // asset borrow から cost 取得
  let remain = remainByAssetBalances
  let cost = costByAssetBalances
  for (const assetBorrow of assetBorrows) {
    // amount = remain の場合
    // - cost に value 追加
    // - amount, value, remain 0 にする
    // - 処理終了
    if (equals(assetBorrow.amount, remain)) {
      cost = plus(cost, assetBorrow.value)
      remain = createBigDecimal(0n)
      assetBorrow.amount = createBigDecimal(0n)
      assetBorrow.value = createBigDecimal(0n)
      break
    }

    // amount > remain の場合
    // - amount から remain 引く
    // - cost に remain 分の value 追加 & value からその分引く
    // - remain 0 にする
    // - 処理終了
    if (greaterThan(assetBorrow.amount, remain)) {
      const valuePerRemain = divide(
        times(assetBorrow.value, remain, 6),
        assetBorrow.amount,
        6,
      )
      assetBorrow.amount = minus(assetBorrow.amount, remain)
      assetBorrow.value = minus(assetBorrow.value, valuePerRemain)
      cost = plus(cost, valuePerRemain)
      remain = createBigDecimal(0n)
      break
    }

    // amount < remain の場合
    // - cost に　value 追加
    // - remain から amount 引く
    // - amount, value 0 にする
    cost = plus(cost, assetBorrow.value)
    remain = minus(remain, assetBorrow.amount)
    assetBorrow.amount = createBigDecimal(0n)
    assetBorrow.value = createBigDecimal(0n)
  }

  if (remain.value > 0n) {
    throw new Error('Insufficient asset')
  }

  return {
    cost,
    assetBalances: newAssetBalances,
    assetBorrows,
    usedBalance: newUsedBalance,
  }
}

const getCostByAssetBalances = ({
  amount,
  assetBalances,
  usedBalance,
  costBasis,
}: {
  readonly amount: BigDecimal
  readonly assetBalances: readonly AssetBalance[]
  readonly usedBalance: AssetBalance
  readonly costBasis: CostBasis
}): {
  readonly remain: BigDecimal
  readonly cost: BigDecimal
  readonly assetBalances: readonly AssetBalance[]
  readonly usedBalance: AssetBalance
} => {
  switch (costBasis) {
    case 'ACB':
    case 'WACB': {
      // asset balances を統合
      const assetBalance =
        assetBalances.length === 1
          ? assetBalances[0]
          : assetBalances.reduce(
              (acc, cur) => ({
                amount: plus(acc.amount, cur.amount),
                value: plus(acc.value, cur.value),
              }),
              { amount: createBigDecimal(0n), value: createBigDecimal(0n) },
            )

      // balance.amount = amount の場合
      // - cost = balance.value
      // - remain = 0
      // - balance.amount, balance.value 0 にして、空配列返す
      // - used balance に balance 追加
      if (equals(assetBalance.amount, amount)) {
        return {
          remain: createBigDecimal(0n),
          cost: assetBalance.value,
          assetBalances: [],
          usedBalance: {
            amount: plus(usedBalance.amount, assetBalance.amount),
            value: plus(usedBalance.value, assetBalance.value),
          },
        }
      }

      // balance.amount > amount の場合
      // - cost = amount * balance.value / balance.amount
      // - remain = 0
      // - balance.amount, balance.value から amount 引く
      // - used balance に amount, cost 追加
      if (greaterThan(assetBalance.amount, amount)) {
        const cost = divide(
          times(assetBalance.value, amount, 6),
          assetBalance.amount,
          6,
        )
        return {
          remain: createBigDecimal(0n),
          cost,
          assetBalances: [
            {
              amount: minus(assetBalance.amount, amount),
              value: minus(assetBalance.value, cost),
            },
          ],
          usedBalance: {
            amount: plus(usedBalance.amount, amount),
            value: plus(usedBalance.value, cost),
          },
        }
      }

      // balance.amount < amount の場合
      // - cost = balance.value
      // - remain = amount - balance.amount
      // - balance.amount, balance.value 0 にして、空配列返す
      // - used balance に balance 追加
      return {
        remain: minus(amount, assetBalance.amount),
        cost: assetBalance.value,
        assetBalances: [],
        usedBalance: {
          amount: plus(usedBalance.amount, assetBalance.amount),
          value: plus(usedBalance.value, assetBalance.value),
        },
      }
    }
    case 'FIFO':
    case 'LIFO': {
      // base asset balances を作成
      const newAssetBalances: AssetBalance[] = []
      let remain = amount
      let cost = createBigDecimal(0n)

      for (const assetBalance of assetBalances) {
        // remain = 0 の場合
        // new asset balances に asset balance 追加
        if (remain.value === 0n) {
          newAssetBalances.push(assetBalance)
          continue
        }

        // amount = remain の場合
        // - cost に value 追加
        // - remain 0 にする
        // - asset balance は削除
        if (equals(assetBalance.amount, remain)) {
          cost = plus(cost, assetBalance.value)
          remain = createBigDecimal(0n)
          continue
        }

        // amount > remain の場合
        // - cost に remain 分の value 追加
        // - remain 0 にする
        // - new asset balances に remain, cost を差し引いた asset balance 追加
        if (greaterThan(assetBalance.amount, remain)) {
          const valuePerRemain = divide(
            times(assetBalance.value, remain, 6),
            assetBalance.amount,
            6,
          )
          cost = plus(cost, valuePerRemain)
          newAssetBalances.push({
            amount: minus(assetBalance.amount, remain),
            value: minus(assetBalance.value, valuePerRemain),
          })
          remain = createBigDecimal(0n)
          break
        }

        // amount < remain の場合
        // - cost に　value 追加
        // - remain から amount 引く
        // - asset balance は削除
        cost = plus(cost, assetBalance.value)
        remain = minus(remain, assetBalance.amount)
      }

      return {
        remain,
        cost,
        assetBalances: newAssetBalances,
        usedBalance: {
          amount: plus(usedBalance.amount, minus(amount, remain)),
          value: plus(usedBalance.value, cost),
        },
      }
    }
    default: {
      throw new Error(`Unknown cost basis: ${costBasis satisfies never}`)
    }
  }
}
